/* ==========================================================================
   ShopyGuru — product.js
   Product gallery, real Shopify variant selection, lightbox, sticky mobile ATC.
   ========================================================================== */
(() => {
  'use strict';

  const S = (window.theme && window.theme.strings) || {};
  const reduceMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------------- Gallery ---------------- */
  const initGallery = (section) => {
    const root = section.querySelector('[data-gallery]');
    const track = root && root.querySelector('[data-gallery-slides]');
    if (!track) return null;
    // In RTL, slide n sits at scrollLeft -n * width
    const rtl = getComputedStyle(track).direction === 'rtl';
    const slides = Array.from(track.children);
    const thumbs = Array.from(root.querySelectorAll('[data-gallery-thumb]'));
    const prev = root.querySelector('[data-gallery-prev]');
    const next = root.querySelector('[data-gallery-next]');
    const counter = root.querySelector('[data-gallery-current]');
    let current = Math.max(0, slides.findIndex((s) => s.classList.contains('is-active')));

    const setActive = (index) => {
      current = index;
      slides.forEach((s, i) => {
        const on = i === index;
        s.classList.toggle('is-active', on);
        if (!on) s.querySelectorAll('video').forEach((v) => v.pause());
      });
      thumbs.forEach((t, i) => {
        const on = i === index;
        t.classList.toggle('is-active', on);
        if (on) t.setAttribute('aria-current', 'true');
        else t.removeAttribute('aria-current');
      });
      if (counter) counter.textContent = String(index + 1);
      if (prev) prev.disabled = index === 0;
      if (next) next.disabled = index === slides.length - 1;
      const thumb = thumbs[index];
      if (thumb) {
        const list = thumb.closest('[data-gallery-thumbs]');
        const item = thumb.parentElement;
        if (list && item) {
          // Measure on screen so centring works in both LTR and RTL
          const l = list.getBoundingClientRect();
          const r = item.getBoundingClientRect();
          list.scrollBy({
            left: (r.left + r.width / 2) - (l.left + l.width / 2),
            top: (r.top + r.height / 2) - (l.top + l.height / 2),
            behavior: reduceMotion() ? 'auto' : 'smooth'
          });
        }
      }
    };

    // While a programmatic scroll is running, ignore intermediate scroll positions
    let pendingTarget = null;
    let pendingTimer;
    const goTo = (index, smooth = true) => {
      const i = Math.max(0, Math.min(slides.length - 1, index));
      pendingTarget = i;
      clearTimeout(pendingTimer);
      pendingTimer = setTimeout(() => { pendingTarget = null; }, 1200);
      track.scrollTo({ left: (rtl ? -i : i) * track.clientWidth, behavior: smooth && !reduceMotion() ? 'smooth' : 'auto' });
      setActive(i);
    };

    // keep state in sync with swipe / trackpad scrolling
    let scrollTimer;
    track.addEventListener('scroll', () => {
      clearTimeout(scrollTimer);
      scrollTimer = setTimeout(() => {
        const i = Math.round(Math.abs(track.scrollLeft) / Math.max(1, track.clientWidth));
        if (pendingTarget !== null) {
          if (i === pendingTarget) pendingTarget = null;
          return;
        }
        if (i !== current) setActive(i);
      }, 90);
    }, { passive: true });

    thumbs.forEach((thumb, i) => {
      thumb.addEventListener('click', () => goTo(i));
      thumb.addEventListener('mouseenter', () => {
        if (window.matchMedia('(hover: hover) and (pointer: fine)').matches) goTo(i, false);
      });
    });
    if (prev) prev.addEventListener('click', () => goTo(current - 1));
    if (next) next.addEventListener('click', () => goTo(current + 1));
    root.addEventListener('keydown', (e) => {
      if (e.target.closest('input, select, textarea')) return;
      const step = rtl ? -1 : 1;
      if (e.key === 'ArrowRight') { e.preventDefault(); goTo(current + step); }
      if (e.key === 'ArrowLeft') { e.preventDefault(); goTo(current - step); }
    });
    window.addEventListener('resize', () => goTo(current, false));

    // Lightbox
    const dialog = section.querySelector('[data-lightbox]');
    root.addEventListener('click', (e) => {
      const zoom = e.target.closest('[data-gallery-zoom]');
      if (!zoom) return;
      const src = zoom.dataset.zoomSrc;
      if (!dialog || typeof dialog.showModal !== 'function') {
        window.open(src, '_blank', 'noopener');
        return;
      }
      const img = dialog.querySelector('[data-lightbox-img]');
      const base = zoom.querySelector('img');
      img.removeAttribute('width');
      img.removeAttribute('height');
      img.src = src;
      img.alt = base ? base.alt : '';
      dialog.showModal();
    });
    if (dialog) {
      const close = dialog.querySelector('[data-lightbox-close]');
      if (close) close.addEventListener('click', () => dialog.close());
      dialog.addEventListener('click', (e) => { if (e.target === dialog) dialog.close(); });
    }

    if (current > 0) requestAnimationFrame(() => goTo(current, false));
    else setActive(0);

    return {
      goToMedia(mediaId) {
        const i = slides.findIndex((s) => s.dataset.mediaId === String(mediaId));
        if (i > -1) goTo(i);
      }
    };
  };

  /* ---------------- Sticky mobile add to cart ---------------- */
  const initStickyAtc = (section) => {
    const bar = section.querySelector('[data-sticky-atc]');
    const mainButton = section.querySelector('[data-product-submit]');
    if (!bar || !mainButton) return;
    const mobile = window.matchMedia('(max-width: 767px)');
    let visible = false;
    let ticking = false;
    const show = (state) => {
      if (state === visible) return;
      visible = state;
      bar.classList.toggle('is-visible', state);
      bar.setAttribute('aria-hidden', state ? 'false' : 'true');
      bar.querySelectorAll('button').forEach((b) => { b.tabIndex = state ? 0 : -1; });
      document.body.classList.toggle('has-sticky-atc', state);
    };
    // Visible on phones once the main Add to cart button has scrolled above the viewport.
    const update = () => {
      ticking = false;
      show(mobile.matches && mainButton.getBoundingClientRect().bottom < 0);
    };
    const onScroll = () => {
      if (!ticking) { ticking = true; window.requestAnimationFrame(update); }
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    update();
  };

  /* ---------------- Variants ---------------- */
  const initProduct = (section) => {
    if (section.dataset.productReady) return;
    section.dataset.productReady = 'true';

    const gallery = initGallery(section);
    initStickyAtc(section);

    const jsonEl = section.querySelector('[data-product-json]');
    if (!jsonEl) return;
    let product;
    try { product = JSON.parse(jsonEl.textContent); } catch (e) { return; }
    const variants = product.variants || [];

    const picker = section.querySelector('[data-variant-picker]');
    const select = section.querySelector('[data-variant-select]');
    const addButton = section.querySelector('[data-product-submit]');
    const addLabel = section.querySelector('[data-product-submit-label]');
    const stickyButton = section.querySelector('[data-sticky-atc-button]');
    const orderButtons = Array.from(section.querySelectorAll('[data-order-now]'));
    const stickyPrice = section.querySelector('[data-sticky-price]');
    const stock = section.querySelector('[data-stock]');
    const quantity = section.querySelector('[data-quantity-input]');
    const errorEl = section.querySelector('[data-form-error]');

    const groups = picker ? Array.from(picker.querySelectorAll('.variant-option')) : [];
    const selectedOptions = () => groups.map((group) => {
      const dropdown = group.querySelector('[data-option-select]');
      if (dropdown) return dropdown.value;
      const checked = group.querySelector('[data-option-input]:checked');
      return checked ? checked.value : null;
    });
    const findVariant = (opts) => variants.find((v) => v.options.every((o, i) => o === opts[i]));

    const setAvailability = (variant) => {
      const available = Boolean(variant && variant.available);
      const label = !variant ? S.unavailable : available ? S.addToCart : S.soldOut;
      [addButton, stickyButton, ...orderButtons].forEach((b) => {
        if (!b) return;
        b.dataset.unavailable = available ? 'false' : 'true';
        if (b.getAttribute('aria-busy') !== 'true') b.disabled = !available;
      });
      if (addLabel) addLabel.textContent = label;
      if (stickyButton) stickyButton.textContent = label;
      if (stock) {
        stock.textContent = !variant ? S.unavailable : variant.available ? (variant.in_stock ? S.inStock : S.available) : S.soldOut;
        stock.classList.toggle('is-available', available);
        stock.classList.toggle('is-sold-out', !available);
      }
      const payment = section.querySelector('.shopify-payment-button');
      if (payment) payment.hidden = !available;
    };

    const setPrice = (variant) => {
      section.querySelectorAll('[data-price]').forEach((price) => {
        const value = price.querySelector('[data-price-value]');
        const compareWrap = price.querySelector('[data-price-compare-wrap]');
        const compare = price.querySelector('[data-price-compare]');
        const badge = price.querySelector('[data-price-badge]');
        const label = price.querySelector('[data-price-label]');
        const onSale = Boolean(variant && variant.compare);
        if (value && variant) value.innerHTML = variant.price;
        if (compareWrap) compareWrap.hidden = !onSale;
        if (compare) compare.innerHTML = onSale ? variant.compare : '';
        if (badge) {
          badge.hidden = !onSale;
          badge.textContent = onSale ? (S.savePercent || '-[percent]%').replace('[percent]', variant.save) : '';
        }
        if (label) label.textContent = onSale ? S.salePrice : S.regularPrice;
        price.classList.toggle('price--on-sale', onSale);
        price.classList.toggle('price--sold-out', !(variant && variant.available));
      });
      if (stickyPrice && variant) stickyPrice.innerHTML = variant.price;
    };

    const setSku = (variant) => {
      document.querySelectorAll('[data-variant-sku]').forEach((el) => {
        el.textContent = variant && variant.sku ? variant.sku : '';
        const wrap = el.closest('[data-variant-sku-wrap]');
        if (wrap) wrap.hidden = !(variant && variant.sku);
      });
    };

    const setOptionStates = (opts) => {
      groups.forEach((group, gi) => {
        const selectedLabel = group.querySelector('[data-option-selected]');
        if (selectedLabel) selectedLabel.textContent = opts[gi] || '';
        group.querySelectorAll('[data-option-input]').forEach((input) => {
          const candidate = opts.slice();
          candidate[gi] = input.value;
          const match = findVariant(candidate);
          const ok = Boolean(match && match.available);
          const label = section.querySelector(`label[for="${input.id}"]`);
          if (!label) return;
          label.classList.toggle('is-unavailable', !ok);
          const sr = label.querySelector('[data-option-unavailable]');
          if (sr) sr.hidden = ok;
          const optionPrice = label.querySelector('[data-option-price]');
          if (optionPrice && match) optionPrice.innerHTML = match.price;
        });
      });
    };

    const onChange = () => {
      const opts = selectedOptions();
      const variant = findVariant(opts);
      if (errorEl) { errorEl.hidden = true; errorEl.textContent = ''; }
      setOptionStates(opts);
      setPrice(variant);
      setAvailability(variant);
      setSku(variant);
      if (!variant) return;
      if (select) select.value = String(variant.id);
      if (quantity) {
        quantity.min = variant.qty_min;
        quantity.step = variant.qty_step;
        if ((parseInt(quantity.value, 10) || 0) < variant.qty_min) quantity.value = variant.qty_min;
      }
      if (variant.media_id && gallery) gallery.goToMedia(variant.media_id);
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState(window.history.state, '', url.toString());
      section.dispatchEvent(new CustomEvent('variant:change', { bubbles: true, detail: { variant } }));
    };

    if (picker) {
      picker.addEventListener('change', onChange);
      setOptionStates(selectedOptions());
    }

    const initial = select ? variants.find((v) => String(v.id) === select.value) : variants[0];
    [addButton, stickyButton, ...orderButtons].forEach((b) => {
      if (b) b.dataset.unavailable = initial && initial.available ? 'false' : 'true';
    });
  };

  const boot = () => document.querySelectorAll('[data-product-section]').forEach(initProduct);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();

  // Theme editor: re-initialise when the section is re-rendered
  document.addEventListener('shopify:section:load', (e) => {
    const section = e.target.querySelector('[data-product-section]');
    if (section) initProduct(section);
  });
})();